import React from 'react'
import useSwr from 'swr'
import { API_URL } from '@/utils/constants'
import { ROUTER_FEE_CAP_PER_1000, ROUTER_FEE_PER_1000, SFAMM_FEE_DENOM, type Pool } from './amm'
import { strictFetcher } from './strict-fetcher'

interface PoolsResponse {
	pools: Pool[]
	height: number | null
	/// OWRouter's fee per 1000, read off chain by the server (op-20 `get_fee`).
	/// Optional because an API pod that predates the field simply omits it.
	router_fee_per_1000?: number
	/// SUBFROSTAMM pools (factory 4:333052). Optional: an API pod that predates
	/// the field omits it, and the terminal then quotes oyl only.
	sfamm_pools?: unknown
}

const isId = (v: unknown): v is string => typeof v === 'string' && /^\d+:\d+$/.test(v)
const isRaw = (v: unknown): v is string => typeof v === 'string' && /^\d+$/.test(v)

/// `sfamm_pools` as quotable `Pool`s, dropping any row that is not one.
///
/// A row is only usable with BOTH token ids, both reserves as decimal strings
/// and a ppm fee below 100% — a SUBFROSTAMM pool quoted at the wrong fee would
/// derive a `min_out` the factory reverts on. Rows are tagged
/// `venue: 'subfrostamm'` so nothing downstream can mistake them for oyl pools.
export function sfammPoolsFrom(value: unknown): Pool[] {
	if (!Array.isArray(value)) {
		return []
	}
	const out: Pool[] = []
	for (const row of value) {
		const r = row as Record<string, unknown> | null
		const t0 = r?.token0 as Record<string, unknown> | undefined
		const t1 = r?.token1 as Record<string, unknown> | undefined
		const fee = r?.fee_ppm
		if (
			!r ||
			!isId(r.pool_id) ||
			!isId(t0?.id) ||
			!isId(t1?.id) ||
			!isRaw(r.reserve0) ||
			!isRaw(r.reserve1) ||
			typeof fee !== 'number' ||
			!Number.isInteger(fee) ||
			fee < 0 ||
			BigInt(fee) >= SFAMM_FEE_DENOM
		) {
			continue
		}
		const token = (t: Record<string, unknown>) => ({
			id: t.id as string,
			name: typeof t.name === 'string' ? t.name : null,
			icon: typeof t.icon === 'string' ? t.icon : null
		})
		out.push({
			pool_id: r.pool_id,
			token0: token(t0 as Record<string, unknown>),
			token1: token(t1 as Record<string, unknown>),
			reserve0: r.reserve0,
			reserve1: r.reserve1,
			total_supply: isRaw(r.total_supply) ? r.total_supply : '0',
			fee_per_1000: 0,
			name: null,
			venue: 'subfrostamm',
			fee_ppm: fee
		})
	}
	return out
}

/// The router fee to quote with, from whatever the server sent.
///
/// Fails in the same direction the server does: a missing field, a 0, a
/// non-integer, or a value past the contract's own 5% cap all fall back to the
/// compiled default rather than to 0. Quoting a fee LOWER than the router
/// really charges is the failure that hurts — `min_out` comes out unreachable,
/// the router refunds inside a successful transaction, and the user sees a swap
/// that silently did nothing. Quoting one too high only under-promises.
export function routerFeeFrom(value: unknown): bigint {
	if (typeof value !== 'number' || !Number.isInteger(value) || value <= 0) {
		return ROUTER_FEE_PER_1000
	}
	const fee = BigInt(value)
	return fee > ROUTER_FEE_CAP_PER_1000 ? ROUTER_FEE_PER_1000 : fee
}

/// Reachability of the pool registry, as three states the UI must tell apart.
/// Before this existed an unreachable registry was INDISTINGUISHABLE from an
/// empty one: the page rendered "Select market", "Price —" and a healthy-looking
/// submit button, and asked the user to pick from a list that could not exist.
///
/// - `ok`    — the last fetch succeeded; everything on screen is current.
/// - `stale` — a fetch failed but an earlier payload is still on screen. Real
///             numbers, just old: a quiet age chip, not an alarm.
/// - `down`  — no payload has ever arrived. Nothing on screen is a price.
export type PoolsHealth = 'ok' | 'stale' | 'down'

export interface PoolsState {
	pools: Pool[]
	/// SUBFROSTAMM pools, normalized (`sfammPoolsFrom`). Never mixed into `pools`.
	sfammPools: Pool[]
	height: number | null
	/// The router's live fee, for the quote math to net out. Always a usable
	/// fee — see `routerFeeFrom`.
	routerFeePer1000: bigint
	isLoading: boolean
	/// The raw SWR error, for callers that want to report the cause.
	error: unknown
	/// Did a pools payload ever arrive? This is what separates "arrived and
	/// empty" (a real, if odd, market state) from "never arrived" (a dead page).
	isReachable: boolean
	health: PoolsHealth
	/// Epoch ms of the most recent successful fetch; null until the first one.
	updatedAt: number | null
	/// Manual revalidation, for a Retry control.
	retry: () => void
}

/// Live SUBFROST AMM pool registry with reserves. Server caches 30s; we poll
/// on the same order so quotes track the chain without hammering anything.
///
/// Call this ONCE per tree and pass the result down. SWR only fires `onSuccess`
/// on the hook that owns the request, so a second instance would silently never
/// stamp `updatedAt`.
export default function usePools(): PoolsState {
	const [updatedAt, setUpdatedAt] = React.useState<number | null>(null)
	const { data, error, isLoading, mutate } = useSwr<PoolsResponse>(
		`${API_URL}/alkane/pools`,
		strictFetcher,
		{
			refreshInterval: 20_000,
			revalidateOnFocus: true,
			// Stamped from the callback rather than an effect so the age is the
			// age of the FETCH, not of the last time the reserves happened to
			// change — SWR keeps the previous object when a payload is identical.
			onSuccess: () => setUpdatedAt(Date.now())
		}
	)

	const retry = React.useCallback(() => {
		void mutate()
	}, [mutate])

	const isReachable = data !== undefined
	const sfammRaw = data?.sfamm_pools
	const sfammPools = React.useMemo(() => sfammPoolsFrom(sfammRaw), [sfammRaw])

	return {
		pools: Array.isArray(data?.pools) ? data.pools : [],
		sfammPools,
		height: data?.height ?? null,
		routerFeePer1000: routerFeeFrom(data?.router_fee_per_1000),
		isLoading,
		error,
		isReachable,
		health: !error ? 'ok' : isReachable ? 'stale' : 'down',
		updatedAt,
		retry
	}
}
